import { useEffect, useState, useRef, useCallback } from 'react';
import { Container, Typography, Box, Button, Grid, Card, CardContent, CardHeader, CircularProgress, Chip, FormControl, InputLabel, Select, MenuItem, TextField, Accordion, AccordionSummary, AccordionDetails, FormControlLabel, Switch, Tooltip, InputAdornment, Alert, Tabs, Tab } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getProperties } from '../services/propertyService';
import { getWorkflowStageCombinations } from '../services/workflowService';
import type { Property } from '../types/property';
import { getPropertyViewMode, canManageBothSalesAndRentals } from '../utils/roleUtils';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { CheckCircle as SoldIcon } from '@mui/icons-material';
import api from '../services/api';

const getDisplayStreet = (address?: Property['address']) => {
  if (!address) {
    return 'No address';
  }
  // Prefer formattedAddress first line (matches app and includes unit/apt e.g. "12/3 Charles St")
  if (address.formattedAddress) {
    const formattedStreet = address.formattedAddress.split(',')[0]?.trim() || '';
    if (formattedStreet) return formattedStreet;
  }
  if (address.street) return address.street;
  const fallback = [address.city, address.state, address.postalCode].filter(Boolean).join(', ');
  return fallback || 'No address';
};

const getDefaultActivePropertyLimit = (planName: string) => {
  switch (planName) {
    case 'Free':
    case 'Agency Free':
      return 1;
    case 'Starter':
      return 10;
    default:
      return undefined;
  }
};

const Properties = () => {
  const navigate = useNavigate();
  const { agency, user, refreshAuth } = useAuth();
  const [properties, setProperties] = useState<Property[]>([]);
  const [total, setTotal] = useState(0);
  const [activePropertyCount, setActivePropertyCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [workflowStageFilter, setWorkflowStageFilter] = useState<string>('all');
  const [assigneeFilter, setAssigneeFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [archivedOnly, setArchivedOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [workflowStages, setWorkflowStages] = useState<string[]>([]);
  const [loadingStages, setLoadingStages] = useState(true);
  const [users, setUsers] = useState<any[]>([]);
  const [showInactiveSubscriptionNotice, setShowInactiveSubscriptionNotice] = useState(true);
  const itemsPerPage = 9;
  const observer = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const isInitialLoadRef = useRef(true);
  const [searching, setSearching] = useState(false);
  const viewMode = getPropertyViewMode(user);
  const showSalesRentalsTabs = canManageBothSalesAndRentals(user);
  // Default tab from role: rental-only → Rental, sales-only or both → Sale
  const [saleTypeTab, setSaleTypeTab] = useState<'sale' | 'rental'>(() =>
    viewMode === 'rental' ? 'rental' : 'sale'
  );
  // Adjust tab when viewMode is not 'both' so it stays in sync with role (e.g. after auth load)
  useEffect(() => {
    if (viewMode === 'rental') setSaleTypeTab('rental');
    else if (viewMode === 'sales') setSaleTypeTab('sale');
  }, [viewMode]);
  const effectiveSaleType: 'sale' | 'rental' | undefined =
    viewMode === 'both' ? saleTypeTab : viewMode === 'rental' ? 'rental' : viewMode === 'sales' ? 'sale' : undefined;
  // Use subscription object if available, fallback to legacy field
  const currentStatus = agency?.subscription?.status || agency?.subscriptionStatus;
  const isSubscriptionActive = currentStatus === 'active' || currentStatus === 'trial';
  const isAdmin = user?.primaryRole === 'admin';

  // Refresh auth state on mount to ensure subscription data is up to date
  useEffect(() => {
    if (agency && !agency.subscription && !agency.subscriptionStatus) {
      // If we have an agency but no subscription data at all, refresh auth state
      refreshAuth();
    }
  }, [agency, refreshAuth]);

  const prevSaleTypeRef = useRef(effectiveSaleType);
  // When switching tabs, clear filters that would hide results for the other type
  useEffect(() => {
    if (effectiveSaleType === 'sale' && (statusFilter === 'rent' || statusFilter === 'leased')) {
      setStatusFilter('all');
    }
    if (prevSaleTypeRef.current !== effectiveSaleType && effectiveSaleType === 'rental') {
      setWorkflowStageFilter('all');
      setAssigneeFilter('all');
    }
    prevSaleTypeRef.current = effectiveSaleType;
  }, [effectiveSaleType, statusFilter]);

  // Fetch workflow stages for filters
  const fetchWorkflowStages = useCallback(async () => {
    if (!agency?.slug) return;
    try {
      setLoadingStages(true);
      const stages = await getWorkflowStageCombinations(agency.slug);
      const filteredStages = stages.filter(stage => {
        const lower = stage.toLowerCase();
        return !lower.includes('rental') && !lower.includes('renting');
      });
      setWorkflowStages(filteredStages);
      if (workflowStageFilter !== 'all' && !filteredStages.includes(workflowStageFilter)) {
        setWorkflowStageFilter('all');
      }
    } catch (err: any) {
      console.error('Failed to fetch workflow stages:', err);
    } finally {
      setLoadingStages(false);
    }
  }, [agency?.slug, workflowStageFilter]);

  // Fetch users for team assignment display
  const fetchUsers = useCallback(async () => {
    if (!agency?.slug) return;
    try {
      const response = await api.get(`/agencies/${agency.slug}/users`);
      setUsers(response.data);
      
      // Set default assignee filter to current user if available
      if (user && response.data.length > 0) {
        const currentUser = response.data.find((u: any) => u._id === user.id);
        if (currentUser) {
          setAssigneeFilter(currentUser._id);
        }
      }
    } catch (err: any) {
      console.error('Failed to fetch users:', err);
    }
  }, [agency?.slug, user]);

  // Fetch properties (paginated)
  const fetchProperties = useCallback(async (reset = false, targetPage?: number) => {
    if (!agency?.slug) return;
    
    const currentPage = targetPage || (reset ? 1 : page);
    
    if (reset) {
      const isInitialLoad = isInitialLoadRef.current;
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setSearching(true);
      }
      setError(null);
      setPage(1);
      setHasMore(true);
    } else {
      setIsLoadingMore(true);
    }
    
    try {
      const data = await getProperties(agency.slug, currentPage, itemsPerPage, archivedOnly, effectiveSaleType);
      if (!archivedOnly) {
        setActivePropertyCount(data.total);
      }
      
      if (reset) {
        setProperties(data.properties);
        setTotal(data.total);
        setHasMore(data.properties.length < data.total);
      } else {
        setProperties(prev => {
          const updated = [...prev, ...data.properties];
          setHasMore(updated.length < data.total);
          return updated;
        });
        setTotal(data.total);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch properties');
    } finally {
      if (reset) {
        if (isInitialLoadRef.current) {
          setLoading(false);
          isInitialLoadRef.current = false;
        } else {
          setSearching(false);
        }
      } else {
        setIsLoadingMore(false);
      }
    }
  }, [agency?.slug, page, itemsPerPage, archivedOnly, effectiveSaleType]);

  // Initial load and on agency change
  useEffect(() => {
    fetchProperties(true);
    fetchWorkflowStages();
    fetchUsers();
    // eslint-disable-next-line
  }, [agency?.slug]);

  // Reset and re-fetch on filter/search change (including role-based saleType tab)
  useEffect(() => {
    setPage(1);
    setProperties([]);
    setHasMore(true);
    fetchProperties(true, 1);
    // eslint-disable-next-line
  }, [statusFilter, workflowStageFilter, assigneeFilter, searchQuery, archivedOnly, effectiveSaleType]);

  // Infinite scroll observer
  useEffect(() => {
    if (isLoadingMore || loading || !hasMore) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new window.IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(prev => prev + 1);
      }
    });
    if (loadMoreRef.current) {
      observer.current.observe(loadMoreRef.current);
    }
    return () => observer.current?.disconnect();
  }, [isLoadingMore, loading, hasMore]);

  // Fetch next page when page changes (but not on first load)
  useEffect(() => {
    if (page === 1) return;
    fetchProperties();
    // eslint-disable-next-line
  }, [page]);

  // Filter properties client-side for search, status, workflow stage, and assignee
  const displayedProperties = properties.filter(property => {
    let matchesStatus = statusFilter === 'all' || property.status === statusFilter;
    let matchesWorkflowStage = workflowStageFilter === 'all' || 
      property.workflowStageCombination === workflowStageFilter;
    let matchesAssignee = assigneeFilter === 'all' || 
      property.primaryAssignee === assigneeFilter ||
      property.secondaryAssignee === assigneeFilter ||
      property.tertiaryAssignee === assigneeFilter;
    let matchesSearch = true;
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const address = property.address;
      matchesSearch = address && (
        address.street?.toLowerCase().includes(query) ||
        address.formattedAddress?.toLowerCase().includes(query) ||
        address.city?.toLowerCase().includes(query) ||
        address.state?.toLowerCase().includes(query) ||
        address.postalCode?.toLowerCase().includes(query)
      );
    }
    
    return matchesStatus && matchesWorkflowStage && matchesAssignee && matchesSearch;
  });

  // Group properties by status
  const groupedProperties = displayedProperties.reduce((groups, property) => {
    const statusKey = property.status || 'Unknown';
    if (!groups[statusKey]) {
      groups[statusKey] = [];
    }
    groups[statusKey].push(property);
    return groups;
  }, {} as Record<string, Property[]>);

  // Define logical order for statuses (workflow order)
  const statusOrder = [
    'consultation',
    'for_sale',
    'pre_auction',
    'auction',
    'cooling_off',
    'under_contract',
    'sold',
    'rent',
    'leased',
    'off_market',
    'withdrawn',
    'cancelled',
    'archived',
    'Unknown'
  ];

  // Sort status sections by logical order
  const sortedStatusKeys = Object.keys(groupedProperties).sort((a, b) => {
    const aIdx = statusOrder.indexOf(a);
    const bIdx = statusOrder.indexOf(b);
    if (aIdx === -1 && bIdx === -1) return a.localeCompare(b);
    if (aIdx === -1) return 1;
    if (bIdx === -1) return -1;
    return aIdx - bIdx;
  });

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  const planName = agency?.subscription?.planName || agency?.subscriptionPlan || 'Agency Free';
  const effectivePlanForLimits = isSubscriptionActive ? planName : 'Agency Free';
  const activePropertyLimit =
    (isSubscriptionActive ? agency?.subscription?.limits?.activeProperties : undefined) ??
    getDefaultActivePropertyLimit(effectivePlanForLimits);
  const hasReachedActivePropertyLimit =
    typeof activePropertyLimit === 'number' && activePropertyCount >= activePropertyLimit;
  const addPropertyDisabled = hasReachedActivePropertyLimit;
  const addPropertyTooltip = hasReachedActivePropertyLimit
    ? `You can manage up to ${activePropertyLimit} active properties on the ${effectivePlanForLimits} plan${isSubscriptionActive ? '' : ' (applied while your paid plan is inactive)'}. Archive an existing property or upgrade to add more.`
    : '';

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography color="error">{error}</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {!isSubscriptionActive && showInactiveSubscriptionNotice && (
        <Box sx={{ mb: 3 }}>
          <Alert
            severity="warning"
            onClose={() => setShowInactiveSubscriptionNotice(false)}
            sx={{ fontWeight: 600 }}
          >
            Your paid subscription isn’t active yet. Property features remain available, but Free plan limits apply until payment succeeds.
          </Alert>
        </Box>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" component="h1">
          {viewMode === 'rental' ? 'Rentals' : viewMode === 'sales' ? 'Sales' : 'Properties'}
        </Typography>
        <Tooltip title={addPropertyTooltip} disableHoverListener={!addPropertyTooltip}>
          <span>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                if (!agency?.slug) return;
                const campaign =
                  effectiveSaleType === 'rental'
                    ? 'rental'
                    : 'sale';
                navigate(`/agencies/${agency.slug}/properties/new?campaign=${campaign}`);
              }}
              disabled={addPropertyDisabled}
            >
              Add New Property
            </Button>
          </span>
        </Tooltip>
      </Box>

      {showSalesRentalsTabs && (
        <Tabs value={saleTypeTab} onChange={(_, v: 'sale' | 'rental') => setSaleTypeTab(v)} sx={{ mb: 2 }}>
          <Tab label="Sales" value="sale" />
          <Tab label="Rentals" value="rental" />
        </Tabs>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardHeader 
              title={archivedOnly ? "Archived Properties" : "Active Properties"} 
              action={
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
                  {/* First row: Search and Toggle */}
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <TextField
                      size="small"
                      placeholder="Search by address..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      sx={{ minWidth: 250, flex: 1 }}
                      InputProps={{
                        endAdornment: searching ? (
                          <InputAdornment position="end">
                            <CircularProgress size={16} />
                          </InputAdornment>
                        ) : undefined
                      }}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={archivedOnly}
                          onChange={(e) => setArchivedOnly(e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Show Archived"
                    />
                  </Box>
                  {/* Second row: Filter dropdowns */}
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <FormControl sx={{ minWidth: 200 }}>
                      <InputLabel>Filter by Status</InputLabel>
                      <Select
                        value={statusFilter}
                        label="Filter by Status"
                        onChange={(e) => setStatusFilter(e.target.value)}
                      >
                        <MenuItem value="all">All Properties</MenuItem>
                        <MenuItem value="consultation">Consultation</MenuItem>
                        <MenuItem value="for_sale">For Sale</MenuItem>
                        <MenuItem value="off_market">Off Market</MenuItem>
                        <MenuItem value="pre_auction">Pre-Auction</MenuItem>
                        <MenuItem value="auction">Auction</MenuItem>
                        <MenuItem value="cooling_off">Cooling Off</MenuItem>
                        <MenuItem value="under_contract">Under Contract</MenuItem>
                        <MenuItem value="sold">Sold</MenuItem>
                        {(effectiveSaleType === 'rental' || (showSalesRentalsTabs && saleTypeTab === 'rental')) && (
                          <>
                            <MenuItem value="rent">Rent</MenuItem>
                            <MenuItem value="leased">Leased</MenuItem>
                          </>
                        )}
                        <MenuItem value="withdrawn">Withdrawn</MenuItem>
                        <MenuItem value="cancelled">Cancelled</MenuItem>
                        <MenuItem value="archived">Archived</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl sx={{ minWidth: 250 }}>
                      <InputLabel>Filter by Workflow Stage</InputLabel>
                      <Select
                        value={workflowStageFilter}
                        label="Filter by Workflow Stage"
                        onChange={(e) => setWorkflowStageFilter(e.target.value)}
                        disabled={loadingStages}
                      >
                        <MenuItem value="all">All Workflow Stages</MenuItem>
                        {workflowStages.map((stage) => (
                          <MenuItem key={stage} value={stage}>
                            {stage}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl sx={{ minWidth: 200 }}>
                      <InputLabel>Filter by Assignee</InputLabel>
                      <Select
                        value={assigneeFilter}
                        label="Filter by Assignee"
                        onChange={(e) => setAssigneeFilter(e.target.value)}
                      >
                        <MenuItem value="all">All Assignees</MenuItem>
                        {users.map((user) => (
                          <MenuItem key={user._id} value={user._id}>
                            {user.firstName} {user.lastName} ({user.primaryRole})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              }
            />
            <CardContent>
              {displayedProperties.length === 0 ? (
                <Typography variant="body1" color="text.secondary">
                  {searchQuery || statusFilter !== 'all' || workflowStageFilter !== 'all' || assigneeFilter !== 'all'
                    ? 'No properties match your search criteria.' 
                    : 'No properties found. Add your first property to get started.'}
                </Typography>
              ) : (
                <>
                  {/* Summary Section */}
                  <Box sx={{ mb: 3, p: 2, backgroundColor: 'rgba(0, 0, 0, 0.02)', borderRadius: 1 }}>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                      Properties Overview
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      <Chip 
                        label={`${displayedProperties.length} total properties`}
                        color="primary"
                        variant="outlined"
                      />
                      <Chip 
                        label={`${sortedStatusKeys.length} property statuses`}
                        color="secondary"
                        variant="outlined"
                      />
                      {sortedStatusKeys.slice(0, 3).map((statusKey) => (
                        <Chip 
                          key={statusKey}
                          label={`${statusKey.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}: ${groupedProperties[statusKey].length}`}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                      {sortedStatusKeys.length > 3 && (
                        <Chip 
                          label={`+${sortedStatusKeys.length - 3} more statuses`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </Box>
                  
                  {/* Status Sections */}
                  {sortedStatusKeys.map((statusKey) => (
                    <Accordion key={statusKey} defaultExpanded sx={{ mb: 2 }}>
                      <AccordionSummary 
                        expandIcon={<ExpandMoreIcon />}
                        sx={{ 
                          backgroundColor: 'rgba(0, 0, 0, 0.03)',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.06)'
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {statusKey.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                            </Typography>
                            <Chip 
                              label={`${groupedProperties[statusKey].length} properties`}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {groupedProperties[statusKey].length === 1 ? 'property' : 'properties'}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 2 }}>
                        <Grid container spacing={2}>
                          {groupedProperties[statusKey].map((property) => (
                            <Grid item xs={12} sm={6} md={4} key={property._id}>
                              <Card sx={{ 
                                height: '100%',
                                transition: 'transform 0.2s, box-shadow 0.2s',
                                '&:hover': {
                                  transform: 'translateY(-2px)',
                                  boxShadow: 3
                                }
                              }}>
                                <CardContent>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, flexWrap: 'wrap' }}>
                                      <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                        {getDisplayStreet(property.address)}
                                      </Typography>
                                      {property.status === 'sold' && (
                                        <Chip
                                          icon={<SoldIcon />}
                                          label="SOLD"
                                          color="success"
                                          size="small"
                                          sx={{ fontWeight: 600 }}
                                        />
                                      )}
                                    </Box>
                                    <Chip 
                                      label={property.currentWorkflowStage || 'No Stage'} 
                                      color={
                                        property.currentWorkflowStage?.toLowerCase().includes('consultation') ? 'warning' :
                                        property.currentWorkflowStage?.toLowerCase().includes('contract') ? 'primary' :
                                        property.currentWorkflowStage?.toLowerCase().includes('settlement') ? 'success' :
                                        property.currentWorkflowStage?.toLowerCase().includes('cooling') ? 'warning' :
                                        property.currentWorkflowStage?.toLowerCase().includes('auction') ? 'secondary' :
                                        property.currentWorkflowStage?.toLowerCase().includes('marketing') ? 'info' :
                                        property.currentWorkflowStage?.toLowerCase().includes('viewing') ? 'info' :
                                        property.currentWorkflowStage?.toLowerCase().includes('offer') ? 'primary' :
                                        property.currentWorkflowStage?.toLowerCase().includes('preparation') ? 'default' :
                                        'default'
                                      }
                                      size="small"
                                    />
                                  </Box>
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    {[
                                      property.address?.city,
                                      property.address?.state,
                                      property.address?.postalCode
                                    ].filter(Boolean).join(', ') || 'No location details'}
                                  </Typography>
                                                                     <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                     {[
                                       property.features?.bedrooms && `${property.features.bedrooms} beds`,
                                       property.features?.bathrooms && `${property.features.bathrooms} baths`,
                                       property.features?.area && `${Number(property.features.area)}m²`
                                     ].filter(Boolean).join(' • ') || 'No features'}
                                   </Typography>
                                   <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontStyle: 'italic' }}>
                                     Created: {property.createdAt ? new Date(property.createdAt).toLocaleDateString('en-AU', {
                                       day: 'numeric',
                                       month: 'short',
                                       year: 'numeric',
                                       hour: '2-digit',
                                       minute: '2-digit'
                                     }) : 'Unknown date'}
                                   </Typography>
                                  {property.primaryAssignee && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                                      Primary: {users.find(u => u._id === property.primaryAssignee)?.firstName + ' ' + 
                                        users.find(u => u._id === property.primaryAssignee)?.lastName || 'Unknown User'}
                                    </Typography>
                                  )}
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => navigate(`/agencies/${agency?.slug}/properties/${property._id}`)}
                                    sx={{ width: '100%' }}
                                  >
                                    View Details
                                  </Button>
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                  {/* Infinite scroll loader */}
                  <div ref={loadMoreRef} />
                  {isLoadingMore && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <CircularProgress size={28} />
                    </Box>
                  )}
                  {!hasMore && displayedProperties.length > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        All properties loaded.
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Properties; 